// Reusable feat choice picker - renders all 5 choice types for feat sub-choices

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { ABILITY_NAMES } from '../../lib/constants.js';
import { getChoiceLabel, getSelectionType, isSpellListChoice, renderMarkdown, slugToTitle } from '../../lib/format.js';

/** @typedef {import('../../types/jsdoc-types.js').ChoiceOption} ChoiceOption */
/** @typedef {import('../../types/jsdoc-types.js').FeatureChoice} FeatureChoice */

/**
 * Renders feat sub-choices (spell list, single spell, single option, multi spell, multi option).
 * Used for both background feat choices and origin feat choices.
 *
 * Events: choice-selected { choiceId, value }, spell-toggled { choiceId, spellSlug }
 */
class FeatChoicePicker extends BaseComponent {
  static properties = {
    choices: { type: Array },
    selections: { type: Object },
    alreadyChosenSpells: { type: Object },
    excludedSpellList: { type: String, attribute: 'excluded-spell-list' },
    recommendedSlugs: { type: Object },
    prefilledChoices: { type: Object },
    radioPrefix: { type: String, attribute: 'radio-prefix' },
    italicLabels: { type: Boolean, attribute: 'italic-labels' },
    useMarkdown: { type: Boolean, attribute: 'use-markdown' },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {FeatureChoice[]} */
    this.choices = [];
    /** @type {{[key: string]: string}} */
    this.selections = {};
    /** @type {Set<string>} */
    this.alreadyChosenSpells = new Set();
    this.excludedSpellList = '';
    /** @type {{[key: string]: string}} */
    this.recommendedSlugs = {};
    /** @type {{[key: string]: string}} */
    this.prefilledChoices = {};
    this.radioPrefix = 'feat';
    this.italicLabels = false;
    this.useMarkdown = false;
  }

  render() {
    return html`${(this.choices || []).map((choice) => this._renderChoice(choice))}`;
  }

  /** @param {FeatureChoice} choice */
  _renderChoice(choice) {
    const choiceId = choice.id || '';
    const prefilled = this.prefilledChoices?.[choiceId];
    if (prefilled) return this._renderPrefilled(choice, choiceId, prefilled);

    const recSlug = this.recommendedSlugs?.[choiceId] || '';
    const selType = getSelectionType(choice);
    const required = selType === 'single' ? 1 : choice.count || 1;
    const selected =
      selType === 'multi'
        ? (this.selections[choiceId]?.split(',').filter(Boolean) || []).length
        : this.selections[choiceId]
          ? 1
          : 0;

    const label = getChoiceLabel(choice) || choiceId;
    const desc = choice.description;

    return html`
      <div class="mb-3">
        ${
          this.italicLabels
            ? html`
              <span class="fst-italic"
                >${label} <span class="badge bg-primary ms-2">${selected} / ${required}</span></span
              >
            `
            : html`
              <div class="fw-medium">${label} <span class="badge bg-primary ms-2">${selected} / ${required}</span></div>
            `
        }
        ${desc ? html`<div class="text-muted small mb-1">${this.useMarkdown ? renderMarkdown(desc) : desc}</div>` : ''}
        ${this._renderOptions(choice, choiceId, recSlug)}
      </div>
    `;
  }

  /**
   * @param {FeatureChoice} choice
   * @param {string} choiceId
   * @param {string} prefilledValue
   */
  _renderPrefilled(choice, choiceId, prefilledValue) {
    const options = Array.isArray(choice.options) ? choice.options : [];
    const option = options.find(
      (/** @type {ChoiceOption | string} */ o) => (typeof o === 'string' ? o : o.slug) === prefilledValue,
    );
    const rawName = option ? (typeof option === 'string' ? option : option.name || option.slug) : prefilledValue;
    return html`
      <div class="mb-3">
        <div class="fw-medium">
          ${getChoiceLabel(choice) || choiceId} <span class="badge bg-primary ms-2">1 / 1</span>
        </div>
        <div class="row g-2 mt-2">
          <div class="col-auto">
            <option-card
              name="${slugToTitle(rawName)}"
              slug="${prefilledValue}"
              ?selected="${true}"
              ?disabled="${true}"
              input-type="radio"
            ></option-card>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * @param {FeatureChoice} choice
   * @param {string} choiceId
   * @param {string} recSlug
   */
  _renderOptions(choice, choiceId, recSlug) {
    if (isSpellListChoice(choice)) return this._renderSpellList(choice, choiceId, recSlug);
    const selType = getSelectionType(choice);
    if (selType === 'single') return this._renderSingle(choice, choiceId, recSlug);
    if (selType === 'multi') return this._renderMulti(choice, choiceId, recSlug);
    return '';
  }

  /**
   * @param {FeatureChoice} choice
   * @param {string} choiceId
   * @param {string} recSlug
   */
  _renderSpellList(choice, choiceId, recSlug) {
    const options = Array.isArray(choice.options) ? choice.options : [];
    return html`
      <div class="row g-2 mt-2">
        ${options.map((/** @type {ChoiceOption | string} */ option) => {
          const slug = typeof option === 'string' ? option : option.slug;
          const name = typeof option === 'string' ? slugToTitle(option) : option.name || option.slug;
          const disabled = slug === this.excludedSpellList;
          return html`
            <div class="col-auto">
              <option-card
                name="${slugToTitle(name)}"
                slug="${slug}"
                ?selected="${this.selections[choiceId] === slug}"
                ?disabled="${disabled}"
                ?recommended="${slug === recSlug}"
                input-type="radio"
                radio-group="${this.radioPrefix}-${choiceId}"
                @card-selected="${(/** @type {CustomEvent} */ e) =>
                  !disabled && this._fireSelected(choiceId, e.detail.slug)}"
              ></option-card>
            </div>
          `;
        })}
      </div>
    `;
  }

  /**
   * @param {FeatureChoice} choice
   * @param {string} choiceId
   * @param {string} recSlug
   */
  _renderSingle(choice, choiceId, recSlug) {
    const alreadyChosen = choice.choiceType === 'spell' ? this.alreadyChosenSpells : new Set();
    const options = Array.isArray(choice.options) ? choice.options : [];
    return html`
      <div class="row g-2 mt-2">
        ${options.map((/** @type {ChoiceOption | string} */ option) => {
          const slug = typeof option === 'string' ? option : option.slug;
          const name = typeof option === 'string' ? slugToTitle(option) : option.name || option.slug;
          const taken = alreadyChosen.has(slug);
          const isSelected = taken || this.selections[choiceId] === slug;

          if (typeof option !== 'string' && option.school) {
            return html`
              <div class="col-12 col-lg-6">
                <spell-option-card
                  name="${name}"
                  slug="${slug}"
                  school="${option.school}"
                  ?concentration="${option.concentration}"
                  description="${option.description || ''}"
                  ?selected="${isSelected}"
                  ?disabled="${taken}"
                  ?known="${taken}"
                  ?recommended="${slug === recSlug}"
                  input-type="radio"
                  radio-group="${this.radioPrefix}-${choiceId}"
                  @card-selected="${(/** @type {CustomEvent} */ e) =>
                    !taken && this._fireSelected(choiceId, e.detail.slug)}"
                ></spell-option-card>
              </div>
            `;
          }

          const displayName = ABILITY_NAMES[name] || name;
          const description = typeof option !== 'string' ? option.description || '' : '';
          return html`
            <div class="col-auto">
              <option-card
                name="${slugToTitle(displayName)}"
                slug="${slug}"
                description="${description}"
                ?selected="${isSelected}"
                ?disabled="${taken}"
                ?known="${taken}"
                ?recommended="${slug === recSlug}"
                input-type="radio"
                radio-group="${this.radioPrefix}-${choiceId}"
                @card-selected="${(/** @type {CustomEvent} */ e) =>
                  !taken && this._fireSelected(choiceId, e.detail.slug)}"
              ></option-card>
            </div>
          `;
        })}
      </div>
    `;
  }

  /**
   * @param {FeatureChoice} choice
   * @param {string} choiceId
   * @param {string} recSlug
   */
  _renderMulti(choice, choiceId, recSlug) {
    const alreadyChosen = choice.choiceType === 'spell' ? this.alreadyChosenSpells : new Set();
    const selected = this.selections[choiceId]?.split(',').filter(Boolean) || [];
    const maxCount = choice.count || 1;
    const options = Array.isArray(choice.options) ? choice.options : [];
    return html`
      <div class="row g-2 mt-1">
        ${options.map((/** @type {ChoiceOption | string} */ option) => {
          const opt = /** @type {ChoiceOption} */ (option);
          const taken = alreadyChosen.has(opt.slug);
          const isSelected = taken || selected.includes(opt.slug);
          const isDisabled = taken || (!isSelected && selected.length >= maxCount);
          return html`
            <div class="col-12 col-md-6 col-lg-4">
              <spell-option-card
                name="${opt.name}"
                slug="${opt.slug}"
                school="${opt.school || ''}"
                ?concentration="${opt.concentration}"
                description="${opt.description || ''}"
                ?selected="${isSelected}"
                ?disabled="${isDisabled}"
                ?known="${taken}"
                ?recommended="${opt.slug === recSlug}"
                input-type="checkbox"
                @card-selected="${(/** @type {CustomEvent} */ e) =>
                  !isDisabled && this._fireToggle(choiceId, e.detail.slug)}"
              ></spell-option-card>
            </div>
          `;
        })}
      </div>
    `;
  }

  /**
   * @param {string} choiceId
   * @param {string} value
   */
  _fireSelected(choiceId, value) {
    this.dispatchEvent(
      new CustomEvent('choice-selected', {
        detail: { choiceId, value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * @param {string} choiceId
   * @param {string} spellSlug
   */
  _fireToggle(choiceId, spellSlug) {
    this.dispatchEvent(
      new CustomEvent('spell-toggled', {
        detail: { choiceId, spellSlug },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

customElements.define('feat-choice-picker', FeatChoicePicker);
